import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { DynamicObjectInterface } from '../../../../sdk/type';
import {
  Accordion,
  AdminPageHeader,
  ButtonStyles,
  DatePicker,
  DropDown,
  DropDownDataInterface,
  DropDownValidations,
  Input,
  InputTypes,
  InputValidations,
  MLPVersion,
  RadioButton,
  Status,
  Tag,
  TagStyle,
  TextArea,
  Theme,
  Toggle,
  ToggleStyle,
} from '../../../../ui/components';
import { LoggerService } from '../../../../ui/utils';
import { convertUTCdate, convertGMT8date } from '../../../../ui/utils/helper';
import RewardService from '../../../../sdk/services/rewardService';
import PinService from '../../../../sdk/services/pinService';
import CampaignService from '../../../../sdk/services/campaignService';
import { useAdminToast } from '../../../../ui/components/AdminToast/AdminToast';
import { useAPICall } from '../../../../hooks';
import { ControlIconType } from '../../../../ui/components/Accordion/Accordion';
import { initialCampaignData } from '../../../../__mockData/initialCampaignData';
import { flattenObject, formatCampaignId } from '../../../../ui/utils/common';
import { DropDownData } from '../../../../ui/components/Form/DropDown/types/propsType';
import styles from './index.module.scss';

interface Validity {
  type: string;
  value: number;
  bufferDays: number;
  bufferMonths: number;
}

interface Pin {
  pinId: string | number;
  pinName: string;
}

interface PinIdSegment {
  id: string | number;
  name: string;
  list: number[];
}

interface SubKpiCode {
  subKpiCode: string;
  points: string;
  targetFrom: string;
  targetTo: string;
}

interface Rule {
  actionType: string;
  kpiCode: string;
  description: string;
  calculationType: string;
  targetFrom: string;
  targetTo: string;
  fullfillmentType: string | null;
  point: string;
  reward: string;
  voucher?: string;
  subKpiCode: SubKpiCode[] | [];
}

interface OrderRule {
  type: string;
  operator: string;
  value: DynamicObjectInterface | string | number;
  combine: string;
}

interface Participant {
  name: string;
  status: string;
  description: string;
  teamSize: number;
  enrollmentType: string;
  validity: Validity;
  pin: Pin[];
  rules: Rule[];
  newAbo?: NewAbo;
}

interface NewAbo {
  isRequired: number | '';
  numOfNewAbo: number | '';
  numOfDays: number | '';
}

interface Order {
  maxOrderNumber: number;
  orderPeriod: number;
  rules: OrderRule[];
  combineRules: string;
}

interface Activities {
  participant: Participant;
  coach: Participant;
  leader: Participant;
  order: Order;
}

export interface CampaignData {
  campaignId: string;
  campaignName: string;
  campaignDescription: string;
  campaignStart: string;
  campaignEnd: string;
  registerEnd?: string;
  loginId: string;
  campaignStatus: string;
  campaignTextLinkDisplay: number;
  campaignTextLink: string;
  activities: Activities;
  [key: string]: DynamicObjectInterface | string | number | Activities;
}

export const campaignStatusList = [
  { value: 'NEW', title: 'NEW' },
  { value: 'ONGOING', title: 'ONGOING' },
  { value: 'CLOSED', title: 'CLOSED' },
];

export const campaignTypeList = [
  { value: 'CHALLENGE', title: 'CHALLENGE' },
  { value: 'PROMOTION', title: 'PROMOTION' },
];

export const campaignGameList = [
  { value: 'scratchWin', title: 'SCRATCH AND WIN' },
  { value: 'spinWheel', title: 'SPIN THE WHEEL' },
];

// Determines which Challenge-mode completion criterion grants a participant their
// redemption chance (see validateChallengeEntitlement on the backend).
export const redemptionCriteriaList = [
  { value: 'COMPLETED_JOURNEY', title: 'Completed Journey' },
  { value: 'INDIVIDUAL_ACHIEVED', title: 'Individual Achieved' },
  { value: 'GROUP_ACHIEVED', title: 'Team Achieved' },
];

export const pinIdSegmentList = [
  {
    name: 'Class 2 (Platinum and Above)',
    id: 1,
    list: [
      330, 340, 342, 345, 365, 367, 370, 375, 380, 385, 390, 395, 400, 405, 410, 415, 420, 425, 430, 435, 440, 445, 450,
      455, 460, 465, 470,
    ],
  },
  {
    name: 'Class 4 (Gold & Silver Producer)',
    id: 2,
    list: [310, 320],
  },
  {
    name: 'Class 6 (Silver Sponsor)',
    id: 3,
    list: [305],
  },
  {
    name: 'Class 9 (ABO - unqualified or below 21%)',
    id: 4,
    list: [0, 203, 206, 209, 212, 215, 218],
  },
  {
    name: 'APC',
    id: 5,
    list: [-1],
  },
];

export const AdminCampaignEditPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { adminToast } = useAdminToast();
  const apiCall = useAPICall();
  const [searchParams] = useSearchParams();
  const recId = searchParams.get('');
  const isNewCampaign = recId === 'new';
  const [toggleValue, setToggleValue] = useState(false);
  const [isPromotion, setIsPromotion] = useState(false);
  const [centralizedLocation, setCentralizedLocation] = useState(false);
  const [isChallenge, setIsChallenge] = useState(false);
  const [challengeList, setChallengeList] = useState<DropDownData[]>([]);
  const deepCopy = JSON.parse(JSON.stringify(initialCampaignData));
  const [campaignData, setCampaignData] = useState<CampaignData>(deepCopy);
  const [rewardList, setRewardList] = useState<DropDownData[]>([]);
  const [pinList, setPinList] = useState([]);
  const [disabledDates, setDisabledDates] = useState<Date[]>([]);
  const [campaignInvalid, setCampaignInvalid] = useState(true);
  const [campaignClickedFields, setCampaignClickedFields] = useState({
    campaignId: false,
    campaignName: false,
    promoChallengeId: false,
    redemptionCriteria: false,
  });
  const [disabledDataRemovalDates, setDisabledDataRemovalDates] = useState<Date[]>([]);

  useEffect(() => {
    if (campaignData?.campaignEnd) {
      const afterDate = new Date(campaignData?.campaignEnd);
      const disabledDataRemovalDates = generateDisabledDates(
        new Date(new Date(afterDate).setHours(0, 0, 0, 0) + 86400000 + (campaignData.hideSidebarDays ?? 0) * 86400000),
        null,
      );
      setDisabledDataRemovalDates(disabledDataRemovalDates);

      if (campaignData?.campaignStart) {
        const beforeDate = new Date(campaignData?.campaignStart);
        const disabledDates = generateDisabledDates(beforeDate, afterDate);
        setDisabledDates(disabledDates);
      }
    }
  }, [campaignData]);

  const fetchRewardList = async () => {
    try {
      const result = await apiCall(RewardService.getRewardList);
      // Filter out inactive rewards and store only rewardCode and rewardName
      const activeRewards = result?.data?.data.filter((reward: DynamicObjectInterface) => reward.status === 'ACTIVE');
      const filteredRewards = activeRewards.map((reward: DynamicObjectInterface) => ({
        rewardCode: reward.rewardCode,
        rewardName: reward.rewardName,
      }));
      setRewardList(filteredRewards);
      // Handle the filtered reward list data as needed
    } catch (error) {
      LoggerService.log('Failed to retrieve user reward list', error);
      // Handle error appropriately
    }
  };
  const fetchPinList = async () => {
    try {
      const result = await apiCall(PinService.getPinList);
      const pinList = result?.data?.data?.map((pin: Pin) => ({
        id: pin.pinId,
        name: pin.pinName,
      }));
      setPinList(pinList);
      // Handle the reward list data as needed
    } catch (error) {
      LoggerService.log('Failed to retrieve user pin list', error);
      // Handle error appropriately
    }
  };

  const fetchChallengeList = async () => {
    try {
      const result = await apiCall(CampaignService.getAllCampaigns);
      if (result?.status === 200 && result?.data?.data?.length > 0) {
        const challenges = result.data.data
          .filter(
            (campaign: DynamicObjectInterface) =>
              campaign.campaignType === 'CHALLENGE' &&
              campaign.status !== 'DELETED' &&
              campaign.status !== 'CLOSED',
          )
          .map((campaign: DynamicObjectInterface) => ({
            title: `${campaign.campaignId} - ${campaign.campaignName}`,
            value: campaign.campaignId,
          }));
        setChallengeList(challenges);
      }
    } catch (error) {
      LoggerService.log('Failed to retrieve active challenge list', error);
    }
  };

  const validateCampaign = () => {
    const {
      campaignId,
      campaignName,
      campaignType,
      registerEnd,
      dataRemovalDate,
      isChallenge,
      promoChallengeId,
      redemptionCriteria,
    } = campaignData ?? {};

    if (campaignType === 'PROMOTION') {
      campaignData.dataRemovalDate = '';
      const isChallengeMissingCampaign = isChallenge === 1 && (!promoChallengeId || !redemptionCriteria);
      const isAnyFieldEmpty = !campaignId || !campaignName || isChallengeMissingCampaign;
      setCampaignInvalid(isAnyFieldEmpty);
    } else {
      const isAnyFieldEmpty = !campaignId || !campaignName || !registerEnd || !dataRemovalDate;
      setCampaignInvalid(isAnyFieldEmpty);
    }
  };

  const handleInputChangeNormal = (name: string, value: DynamicObjectInterface | DynamicObjectInterface[]) => {
    setCampaignData((prevCampaignData: CampaignData | null) => {
      if (!prevCampaignData) {
        // If prevCampaignData is null, return an initial object
        return {
          [name]: value,
        } as CampaignData;
      }

      // Create a copy of the previous campaign data to avoid mutation
      const newData = { ...prevCampaignData };

      // Use a type assertion to handle the dynamic property assignment
      let currentLevel = newData;
      const keys = name.split('.');

      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (!currentLevel[key]) {
          // Initialize an empty object if the key doesn't exist
          currentLevel[key] = {};
        }
        currentLevel = currentLevel[key];
      }

      currentLevel[keys[keys.length - 1]] = value;

      return { ...newData };
    });

    setCampaignClickedFields(prevClickedFields => ({
      ...prevClickedFields,
      [name]: value === '',
    }));

    if (name === 'campaignType') {
      if (value === 'PROMOTION') {
        setIsPromotion(true);
      } else {
        setIsPromotion(false);
      }
    }

    if (name === 'registerEnd') {
      const campaignStart = new Date(campaignData?.campaignStart);
      const campaignEnd = new Date(campaignData?.campaignEnd);
      if (value < campaignStart || value > campaignEnd) {
        setCampaignInvalid(true);
      }
    } else {
      validateCampaign();
    }
  };

  const handleInputChangeFlatten = (name: string, value: DynamicObjectInterface | DynamicObjectInterface[]) => {
    // Determine if the value should be treated as an array
    const isArrayValue = Array.isArray(value) || (typeof value === 'string' && value.includes(','));

    // Convert the string to an array if necessary
    if (isArrayValue && typeof value === 'string') {
      value = value.split(',').map(item => item.trim());
    }

    // Convert array notation to dot notation using flattenObject
    const flattenedName = flattenObject({ [name]: value });
    const flattenedKey = Object.keys(flattenedName)[0];
    if (!flattenedKey) {
      LoggerService.log('Flattened key is undefined or empty.');
      return;
    }

    setCampaignData((prevCampaignData: CampaignData | null) => {
      if (!prevCampaignData) {
        // If prevCampaignData is null, return an initial object
        return {
          [flattenedKey]: value,
        } as CampaignData;
      }

      // Create a copy of the previous campaign data to avoid mutation
      const newData = { ...prevCampaignData };
      let currentLevel: DynamicObjectInterface = newData;
      const keys = flattenedKey.split('.');

      // Traverse the object structure based on the keys
      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];

        // Ensure the current level is an object or array
        if (typeof currentLevel[key] !== 'object' || currentLevel[key] === null) {
          currentLevel[key] = {}; // or [] if you expect an array
        }

        currentLevel = currentLevel[key];
      }

      // Ensure the final key is not a read-only property of a string
      if (typeof currentLevel === 'object' && currentLevel !== null) {
        currentLevel[keys[keys.length - 1]] = value;
      } else {
        LoggerService.log(
          `Cannot assign value to key ${keys[keys.length - 1]} because current level is not an object: ${currentLevel}`,
        );
        return newData;
      }

      return { ...newData };
    });

    validateCampaign();
  };

    const getPinsFromSelectedSegment = (
    selectedSegments: Pin[],
    pinIdSegmentList: PinIdSegment[],
    fullPinList: { id: number; name: string }[],
  ): Pin[] => {
    const allSegmentPinIds = selectedSegments.flatMap(selected => {
      const matchedSegment = pinIdSegmentList.find(segment => segment.id === selected.pinId);
      return matchedSegment?.list ?? [];
    });

    // Deduplicate pinIds just in case
    const uniquePinIds = Array.from(new Set(allSegmentPinIds));
    return fullPinList
      .filter(pin => uniquePinIds.includes(pin.id))
      .map(pin => ({
        pinId: pin.id,
        pinName: pin.name,
      }));
  };

  const generateDisabledDates = (beforeDate: Date, afterDate: Date | null): Date[] => {
    const disabledDates: Date[] = [];

    // Ensure 'beforeDate' and 'afterDate' are at midnight
    beforeDate.setHours(0, 0, 0, 0);
    afterDate?.setHours(0, 0, 0, 0);

    // Collect all dates before 'beforeDate'
    let inputDate = new Date(beforeDate);
    inputDate.setDate(inputDate.getDate() - 1);

    // Set the earliest date to consider
    const oneYearBefore = new Date(inputDate);
    oneYearBefore.setFullYear(oneYearBefore.getFullYear() - 1);

    while (inputDate >= oneYearBefore) {
      disabledDates.push(new Date(inputDate));
      inputDate.setDate(inputDate.getDate() - 1);
    }

    if (afterDate) {
      // Collect all dates after 'afterDate'
      let futureDate = new Date(afterDate);
      futureDate.setDate(futureDate.getDate() + 1);

      // Set the future limit to one year after 'afterDate'
      const futureLimit = new Date(afterDate);
      futureLimit.setFullYear(afterDate.getFullYear() + 1);

      while (futureDate <= futureLimit) {
        disabledDates.push(new Date(futureDate));
        futureDate.setDate(futureDate.getDate() + 1);
      }
    }

    // Convert to timestamp for comparison and uniqueness
    const uniqueDisabledDates = Array.from(new Set(disabledDates.map(date => date.getTime())));

    return uniqueDisabledDates.map(time => new Date(time));
  };

  useEffect(() => {
    if (campaignData?.campaignStart && campaignData?.campaignEnd) {
      const beforeDate = new Date(campaignData?.campaignStart);
      const afterDate = new Date(campaignData?.campaignEnd);
      const disabledDates = generateDisabledDates(beforeDate, afterDate);
      setDisabledDates(disabledDates);
    }
  }, [campaignData]);

  const onSave = async () => {
    const payload = {
      campaignId: campaignData?.campaignId,
      campaignName: campaignData?.campaignName,
      campaignDescription: campaignData?.campaignDescription,
      campaignTextLinkDisplay: campaignData?.campaignTextLinkDisplay,
      campaignTextLink: campaignData?.campaignTextLink,
      campaignStart: new Date(campaignData?.campaignStart).toISOString().slice(0, -1),
      campaignEnd: new Date(campaignData?.campaignEnd).toISOString().slice(0, -1),
      registerEnd: campaignData?.registerEnd ? convertUTCdate(campaignData.registerEnd) : '',
      loginId: 'admin',
      campaignStatus: campaignData?.campaignStatus,
      campaignType: campaignData?.campaignType,
      gameType: isPromotion ? campaignData?.gameType : '',
      productSKU: isPromotion && campaignData?.isChallenge !== 1 ? campaignData?.productSKU : '',
      centralizedLocation: isPromotion && campaignData?.isChallenge ? 1 : campaignData?.centralizedLocation ? 1 : 0,
      isChallenge: isPromotion && campaignData?.isChallenge === 1 ? 1 : 0,
      promoChallengeId: isPromotion && campaignData?.isChallenge === 1 ? campaignData?.promoChallengeId : '',
      redemptionCriteria: isPromotion && campaignData?.isChallenge === 1 ? campaignData?.redemptionCriteria : '',
      hideSidebarDays: campaignData?.hideSidebarDays ? Number(campaignData?.hideSidebarDays) : null,
      dataRemovalDate: campaignData?.dataRemovalDate
        ? moment(campaignData?.dataRemovalDate).format('YYYY-MM-DD')
        : null,
      postAssessmentReminderDays: campaignData?.postAssessmentReminderDays
        ? Number(campaignData?.postAssessmentReminderDays)
        : null,
      activities: {
        participant: {
          name: campaignData?.activities.participant.name,
          description: campaignData?.activities.participant.description,
          status: campaignData?.activities.participant.status,
          teamSize: campaignData?.activities.participant.teamSize,
          enrollmentType: 'PARTICIPANT',
          newAbo: {
            isRequired: campaignData?.activities?.participant?.newAbo?.isRequired ?? 0,
            numOfDays: campaignData?.activities?.participant?.newAbo?.numOfDays ?? '',
            numOfNewAbo: campaignData?.activities?.participant?.newAbo?.numOfNewAbo ?? '',
          },
          validity: {
            type: 'DAYS',
            value: campaignData?.activities.participant.validity.value,
            bufferDays: 15,
            bufferMonths: 0,
          },
          pin: getPinsFromSelectedSegment(campaignData?.activities.participant.pin, pinIdSegmentList, pinList),
          rules: [
            {
              actionType: 'WEIGHT_IN',
              kpiCode: 'WEIGHT_IN_TEAM',
              description: campaignData?.activities.participant.rules[0].description,
              calculationType: 'ONETIME',
              targetFrom: '3',
              point: '',
              targetTo: '0',
              fullfillmentType: null,
              reward: campaignData?.activities.participant.rules[0].reward,
              subKpiCode: [],
            },
            {
              actionType: 'WEIGHT_IN',
              kpiCode: 'NEW_ABO_TEAM',
              description: campaignData?.activities.participant.rules[0].description,
              calculationType: 'ONETIME',
              targetFrom: '1',
              point: '',
              targetTo: '0',
              fullfillmentType: null,
              reward: campaignData?.activities.participant.rules[1].reward,
              subKpiCode: [],
            },
            {
              actionType: 'WEIGHT_OUT',
              kpiCode: 'WEIGHT_OUT_TEAM',
              description: campaignData?.activities.participant.rules[2].description,
              calculationType: 'ONETIME',
              targetFrom: '3',
              targetTo: '0',
              point: '',
              fullfillmentType: null,
              reward: campaignData?.activities.participant.rules[2].reward,
              subKpiCode: [],
            },
            {
              actionType: 'WEIGHT_OUT',
              kpiCode: 'WEIGHT_ACHIEVEMENT_TEAM',
              description: campaignData?.activities.participant.rules[3].description,
              calculationType: 'ONETIME',
              targetFrom: '0',
              targetTo: '0',
              fullfillmentType: 'EITHER',
              point: '',
              reward: campaignData?.activities.participant.rules[3].reward,
              voucher: campaignData?.activities.participant.rules[3].voucher,
              subKpiCode: [
                {
                  subKpiCode: 'WEIGHT_LOST',
                  points: '0',
                  targetFrom: campaignData?.activities.participant.rules[3].subKpiCode[0].targetFrom,
                  targetTo: '0',
                },
                {
                  subKpiCode: 'BODY_FAT_LOST',
                  points: '0',
                  targetFrom: campaignData?.activities.participant.rules[3].subKpiCode[1].targetFrom,
                  targetTo: '0',
                },
                {
                  subKpiCode: 'MUSCLE_MASS_GAIN',
                  points: '0',
                  targetFrom: campaignData?.activities.participant.rules[3].subKpiCode[2].targetFrom,
                  targetTo: '0',
                },
              ],
            },
            {
              actionType: 'WEIGHT_OUT',
              kpiCode: 'WEIGHT_ACHIEVEMENT',
              description: '',
              calculationType: 'POINT',
              targetFrom: '0',
              targetTo: '0',
              fullfillmentType: 'EITHER',
              point: '1',
              reward: '',
              voucher: '',
              subKpiCode: [
                {
                  subKpiCode: 'WEIGHT_LOST',
                  points: '0',
                  targetFrom: campaignData?.activities.participant.rules[4].subKpiCode[0].targetFrom,
                  targetTo: '0',
                },
                {
                  subKpiCode: 'BODY_FAT_LOST',
                  points: '0',
                  targetFrom: campaignData?.activities.participant.rules[4].subKpiCode[1].targetFrom,
                  targetTo: '0',
                },
                {
                  subKpiCode: 'MUSCLE_MASS_GAIN',
                  points: '0',
                  targetFrom: campaignData?.activities.participant.rules[4].subKpiCode[2].targetFrom,
                  targetTo: '0',
                },
              ],
            },
          ],
        },
        coach: {
          name: campaignData?.activities.coach.name,
          description: campaignData?.activities.coach.description,
          status: campaignData?.activities.coach.status,
          teamSize: 0,
          enrollmentType: 'COACH',
          validity: {
            type: 'MONTHS',
            value: campaignData?.activities.coach.validity.value,
            bufferDays: 15,
            bufferMonths: 2,
          },
          pin: getPinsFromSelectedSegment(campaignData?.activities.coach.pin, pinIdSegmentList, pinList),
          rules: [
            {
              actionType: 'COACH_PROCESS',
              kpiCode: 'NEW_ABO_COUNT',
              description: '',
              calculationType: 'COUNT',
              point: '',
              targetFrom: campaignData?.activities.coach.rules[0].targetFrom,
              targetTo: '0',
              fullfillmentType: null,
              reward: null,
              subKpiCode: [],
            },
            {
              actionType: 'COACH_PROCESS',
              kpiCode: 'COMPLETED_COUNT',
              description: '',
              calculationType: 'COUNT',
              point: '',
              targetFrom: campaignData?.activities.coach.rules[1].targetFrom,
              targetTo: '0',
              fullfillmentType: null,
              reward: null,
              subKpiCode: [],
            },
            {
              actionType: 'COACH_PROCESS',
              kpiCode: 'ACHIEVED_COUNT',
              calculationType: 'COUNT',
              description: '',
              point: '',
              targetFrom: campaignData?.activities.coach.rules[2].targetFrom,
              targetTo: '0',
              fullfillmentType: null,
              reward: null,
              subKpiCode: [],
            },
            {
              actionType: 'COACH_PROCESS',
              kpiCode: 'ALP_COUNT',
              calculationType: 'COUNT',
              description: '',
              point: '',
              targetFrom: campaignData?.activities.coach.rules[3].targetFrom,
              targetTo: '0',
              fullfillmentType: null,
              reward: null,
              subKpiCode: [],
            },
          ],
        },
        leader: {
          name: campaignData?.activities.leader.name,
          description: campaignData?.activities.leader.description,
          status: campaignData?.activities.leader.status,
          teamSize: 0,
          enrollmentType: 'LEADER',
          validity: {
            type: 'MONTHS',
            value: campaignData?.activities.leader.validity.value,
            bufferDays: 15,
            bufferMonths: 0,
          },
          pin: getPinsFromSelectedSegment(campaignData?.activities.leader.pin, pinIdSegmentList, pinList),
          rules: [
            {
              actionType: 'LEADER_PROCESS',
              kpiCode: 'QUALIFIED_COACH',
              calculationType: 'COUNT',
              description: '',
              targetFrom: campaignData?.activities.leader.rules[0].targetFrom,
              targetTo: '0',
              point: '',
              fullfillmentType: null,
              reward: 'TRIP2',
              subKpiCode: [],
            },
            {
              actionType: 'LEADER_PROCESS',
              kpiCode: 'NEW_ABO_COUNT_LEADER',
              calculationType: 'COUNT',
              description: '',
              targetFrom: '0',
              targetTo: '0',
              point: '',
              fullfillmentType: null,
              reward: '',
              subKpiCode: [],
            },
            {
              actionType: 'LEADER_PROCESS',
              kpiCode: 'NEW_GROUP_ABO_COUNT_LEADER',
              calculationType: 'COUNT',
              description: '',
              targetFrom: '0',
              targetTo: '0',
              point: '',
              fullfillmentType: null,
              reward: '',
              subKpiCode: [],
            },
            {
              actionType: 'LEADER_PROCESS',
              kpiCode: 'CUMULATIVE_POINTS_LEADER',
              calculationType: 'POINT',
              description: '',
              targetFrom: '0',
              targetTo: '0',
              point: '',
              fullfillmentType: null,
              reward: '',
              subKpiCode: [],
            },
          ],
        },
        order: {
          maxOrderNumber: Number(campaignData?.activities.order.maxOrderNumber),
          orderPeriod: campaignData?.activities.order.orderPeriod,
          rules: [
            {
              type: 'amount',
              operator: '>',
              value: {
                amount: campaignData?.activities.order.rules[0].value.amount,
                currency: 'SGD',
              },
              combine: 'and',
            },
            {
              type: 'bundle',
              operator: 'in',
              value: campaignData?.activities.order.rules[1].value,
              combine: 'AND',
            },
            {
              type: 'item',
              operator: 'and',
              value: {
                base: campaignData?.activities.order.rules[2].value.base,
                addOn: campaignData?.activities.order.rules[2].value.addOn,
              },
              combine: 'or',
            },
          ],
          combineRules: 'and',
        },
      },
    };

    try {
      let result;

      if (isNewCampaign) {
        result = await apiCall(CampaignService.addCampaign, payload);
      } else {
        result = await apiCall(CampaignService.updateCampaign, `${recId}`, payload);
      }

      if (!result?.data?.result) {
        // Handle duplicate campaign ID error
        adminToast({
          message: result?.data?.message ?? t('admin.campaignConfig.duplicateCampaignId'),
          theme: Theme.ERROR,
        });
      } else {
        // Success case
        navigate(`/admin/campaign-configuration/list`);
        adminToast({
          message: isNewCampaign
            ? t('admin.campaignConfig.createCampaignSuccess')
            : t('admin.campaignConfig.updateCampaignSuccess'),
          theme: Theme.SUCCESS,
        });
      }
    } catch (error) {
      LoggerService.log('Error saving campaign', error);
      adminToast({
        message: t('admin.campaignConfig.errorSaveCampaign'),
        theme: Theme.ERROR,
      });
    }
  };

  const buttonConfig = [
    {
      title: t('admin.userEdit.cancel'),
      buttonStyle: ButtonStyles.SOLID,
      onClick: () => {
        navigate(`/admin/campaign-configuration/list`);
      },
    },
    {
      title: t('admin.userEdit.save'),
      buttonStyle: ButtonStyles.SOLID,
      disabled: campaignInvalid,
      onClick: () => {
        if (!formatCampaignId(campaignData?.campaignId)) {
          adminToast({
            message: t('Only letters (A-Z), numbers, and underscores (_) are allowed in Campaign ID.'),
            theme: Theme.ERROR,
          });
          return;
        }

        if (isPromotion) {
          onSave();
        } else {
          const campaignEndDatePlusExtra = new Date(
            new Date(campaignData?.campaignEnd).setHours(0, 0, 0, 0) +
              86400000 +
              (campaignData.hideSidebarDays ?? 0) * 86400000,
          );

          if (
            !campaignData?.registerEnd ||
            new Date(campaignData?.registerEnd) < new Date(campaignData?.campaignStart) ||
            new Date(campaignData?.registerEnd) > new Date(campaignData?.campaignEnd)
          ) {
            adminToast({
              message: t(
                'Unable to save changes. The Registration End Date should be between the Campaign Start Date and Campaign End Date',
              ),
              theme: Theme.ERROR,
            });
            return;
          }

          if (
            !campaignData?.dataRemovalDate ||
            new Date(campaignData?.dataRemovalDate) < new Date(campaignEndDatePlusExtra)
          ) {
            adminToast({
              message: t(
                'Unable to save changes. The Data Removal Date should be after Campaign End Date and Challenge hidden from sidebar',
              ),
              theme: Theme.ERROR,
            });
            return;
          }

          onSave();
        }
      },
    },
  ];
  const fetchData = async () => {
    try {
      const result = await apiCall(CampaignService.getCampaignDetail, `${recId}`);
      const campaignData = result?.data?.data;
      // Check if registerEnd exists and convert it
      if (campaignData?.registerEnd) {
        campaignData.registerEnd = convertGMT8date(campaignData.registerEnd);
      }

      if (campaignData?.campaignType === 'PROMOTION') {
        setIsPromotion(true);
      }

      if (campaignData?.isChallenge === 1) {
        setIsChallenge(true);
      }

      if (campaignData?.activities?.participant?.pin) {
        const selectedPinIds = campaignData.activities.participant.pin.map((pin: Pin) => pin.pinId);
        const pinSegments: Pin[] = [];

        pinIdSegmentList.forEach(segment => {
          const hasAny = segment.list.some(pinId => selectedPinIds.includes(pinId));
          if (hasAny) {
            pinSegments.push({
              pinId: segment.id,
              pinName: segment.name,
            });
          }
        });

        campaignData.activities.participant.pin = pinSegments;
      }

      if (campaignData?.activities?.coach?.pin) {
        const selectedPinIds = campaignData.activities.coach.pin.map((pin: Pin) => pin.pinId);
        const pinSegments: Pin[] = [];

        pinIdSegmentList.forEach(segment => {
          const hasAny = segment.list.some(pinId => selectedPinIds.includes(pinId));
          if (hasAny) {
            pinSegments.push({
              pinId: segment.id,
              pinName: segment.name,
            });
          }
        });

        campaignData.activities.coach.pin = pinSegments;
      }

      if (campaignData?.activities?.leader?.pin) {
        const selectedPinIds = campaignData.activities.leader.pin.map((pin: Pin) => pin.pinId);
        const pinSegments: Pin[] = [];

        pinIdSegmentList.forEach(segment => {
          const hasAny = segment.list.some(pinId => selectedPinIds.includes(pinId));
          if (hasAny) {
            pinSegments.push({
              pinId: segment.id,
              pinName: segment.name,
            });
          }
        });

        campaignData.activities.leader.pin = pinSegments;
      }
      setCampaignData(campaignData);
    } catch (error) {
      LoggerService.log('Failed to retrieve campaign details', error);
      // Handle error appropriately
    }
  };

  useEffect(() => {
    validateCampaign();
  }, [campaignData]);

  useEffect(() => {
    if (!isPromotion) {
      fetchRewardList();
      fetchPinList();
    } else {
      fetchChallengeList();
    }

    if (!isNewCampaign) {
      fetchData();
    }
  }, [recId, isNewCampaign, isPromotion]);

  return (
    <div className={styles['campaign-container']}>
      <AdminPageHeader
        title={isNewCampaign ? t('admin.campaignConfig.newTitle') : t('admin.campaignConfig.editTitle')}
        button={buttonConfig}
      />
      {/* campaign section  */}
      {/* 1st row info  */}
      <div className={styles['edit-user-form-container']}>
        <div className={styles['input-container']}>
          <Input
            type={InputTypes.TEXT}
            name={'campaignId'}
            placeholder={t('admin.campaignConfig.campaignId')}
            label={`${t('admin.campaignConfig.campaignId')}*`}
            disabled={!isNewCampaign}
            value={campaignData?.campaignId}
            mlpVersion={MLPVersion.ONE}
            onChange={e => {
              const trimmedValue = e.target.value.trim();
              handleInputChangeNormal('campaignId', trimmedValue);
            }}
            onClick={() =>
              setCampaignClickedFields(prevClickedFields => ({
                ...prevClickedFields,
                campaignId: campaignData?.campaignId === '',
              }))
            }
            labelInfo="Do not include any spacing in the campaign ID"
            status={campaignClickedFields.campaignId ? InputValidations.INVALID : InputValidations.DEFAULT}
            message={campaignClickedFields.campaignId ? t('admin.campaignConfig.campaignIdReq') : ''}
          />
        </div>
        <div className={styles['input-container']}>
          <DropDown
            name={'campaignType'}
            dropdownLabel={`${t('admin.campaignConfig.campaignType')}`}
            mlpVersion={MLPVersion.ONE}
            data={campaignTypeList}
            value={campaignData?.campaignType}
            configData={{
              label: 'title',
              onSelect: (selectedOptions: DropDownDataInterface) => {
                handleInputChangeNormal('campaignType', selectedOptions.title);
              },
            }}
          />
        </div>
        <div className={styles['input-container']}>
          <Input
            type={InputTypes.TEXT}
            name={'campaignName'}
            placeholder={t('admin.campaignConfig.campaignName')}
            label={`${t('admin.campaignConfig.campaignName')}*`}
            value={campaignData?.campaignName}
            mlpVersion={MLPVersion.ONE}
            onChange={e => {
              handleInputChangeNormal('campaignName', e.target.value);
            }}
            onClick={() =>
              setCampaignClickedFields(prevClickedFields => ({
                ...prevClickedFields,
                campaignName: campaignData?.campaignName === '',
              }))
            }
            status={campaignClickedFields.campaignName ? InputValidations.INVALID : InputValidations.DEFAULT}
            message={campaignClickedFields.campaignName ? t('admin.campaignConfig.campaignNameReq') : ''}
          />
        </div>
        <div className={styles['input-container']}>
          <DatePicker
            inputContainerHeight="36px"
            showDateContainer
            calendarHeading={t('admin.campaignConfig.startDate')}
            value={new Date(campaignData?.campaignStart)}
            onChange={date => {
              handleInputChangeNormal('campaignStart', date);
            }}
            actionButtonStyle={{ padding: '5px 10px 0' }}
            menuContainerStyle={{ display: 'flex', justifyContent: 'flex-end' }}
            calendarContainerStyling={{ padding: '10px', top: '5px' }}
            containerWidth="450px"
            timeLabel={t('admin.campaignConfig.selectTime')}
            timeLabelEnd={t('admin.campaignConfig.selectTimeEnd')}
            timeLabelStart={t('admin.campaignConfig.selectTimeStart')}
            inputLabel={`${t('admin.campaignConfig.startDate')}*`}
          />
        </div>

        <div className={styles['input-container']}>
          <DatePicker
            showDateContainer
            inputContainerHeight="36px"
            calendarHeading={t('admin.campaignConfig.endDate')}
            value={new Date(campaignData?.campaignEnd)}
            onChange={date => {
              if (date) {
                handleInputChangeNormal('campaignEnd', date);
                const dataRemovalDate = new Date(date.getTime());
                dataRemovalDate.setDate(
                  dataRemovalDate.getDate() +
                    180 +
                    (campaignData?.hideSidebarDays ? parseInt(campaignData?.hideSidebarDays) : 0),
                );
                handleInputChangeNormal('dataRemovalDate', dataRemovalDate);
              }
            }}
            containerWidth="450px"
            menuContainerStyle={{ display: 'flex', justifyContent: 'flex-end' }}
            actionButtonStyle={{ padding: '5px 10px 0' }}
            calendarContainerStyling={{ padding: '10px', top: '-150px' }}
            timeLabel={t('admin.campaignConfig.selectTime')}
            timeLabelEnd={t('admin.campaignConfig.selectTimeEnd')}
            timeLabelStart={t('admin.campaignConfig.selectTimeStart')}
            inputLabel={`${t('admin.campaignConfig.endDate')}*`}
          />
        </div>

        <div className={styles['input-container']}>
          <DropDown
            name={'campaignStatus'}
            dropdownLabel={`${t('admin.campaignConfig.campaignStatus')}`}
            mlpVersion={MLPVersion.ONE}
            data={campaignStatusList}
            value={campaignData?.campaignStatus}
            configData={{
              label: 'title',
              onSelect: (selectedOptions: DropDownDataInterface) => {
                handleInputChangeNormal('campaignStatus', selectedOptions.title);
              },
            }}
          />
        </div>
        {isPromotion ? (
          <div className={styles['input-container']}>
            <DropDown
              name={'gameType'}
              dropdownLabel={`${t('admin.campaignConfig.gameType')}`}
              mlpVersion={MLPVersion.ONE}
              data={campaignGameList}
              value={campaignGameList.find(game => game.value === campaignData?.gameType)?.title || campaignData?.gameType}
              configData={{
                label: 'title',
                onSelect: (selectedOptions: DropDownDataInterface) => {
                  handleInputChangeNormal('gameType', selectedOptions.value);
                },
              }}
            />
          </div>
        ) : (
          <div className={styles['input-container']}>
            <DatePicker
              inputContainerHeight="36px"
              showDateContainer
              showTime
              calendarHeading={t('admin.campaignConfig.registrationEnd')}
              value={campaignData?.registerEnd ? new Date(campaignData.registerEnd) : undefined}
              onChange={datetime => {
                handleInputChangeNormal('registerEnd', datetime);
              }}
              actionButtonStyle={{ padding: '5px 10px 0' }}
              menuContainerStyle={{ display: 'flex', justifyContent: 'flex-end' }}
              calendarContainerStyling={{ padding: '10px', top: '-120px' }}
              containerWidth="450px"
              timeLabel={t('admin.campaignConfig.selectTime')}
              timeLabelEnd={t('admin.campaignConfig.selectTimeEnd')}
              timeLabelStart={t('admin.campaignConfig.selectTimeStart')}
              inputLabel={`${t('admin.campaignConfig.registrationEnd')}*`}
              disabledDates={disabledDates}
            />
          </div>
        )}
      </div>
      {/* 2nd row description only  */}
      <div className={styles['edit-user-form-container']}>
        <div className={styles['input-container']}>
          <TextArea
            mlpVersion={MLPVersion.ONE}
            placeholder={t('admin.campaignConfig.description')}
            label={t('admin.campaignConfig.description')}
            resize={false}
            value={campaignData?.campaignDescription}
            onChange={e => {
              handleInputChangeNormal('campaignDescription', e.target.value);
            }}
          />
        </div>
        {!isPromotion && (
          <div className={styles['input-container']}>
            <DatePicker
              showDateContainer
              inputContainerHeight="36px"
              calendarHeading={`Data Removal Date`}
              value={campaignData?.dataRemovalDate ? new Date(campaignData?.dataRemovalDate) : undefined}
              onChange={date => {
                handleInputChangeNormal('dataRemovalDate', date);
              }}
              containerWidth="450px"
              menuContainerStyle={{ display: 'flex', justifyContent: 'flex-end' }}
              actionButtonStyle={{ padding: '5px 10px 0' }}
              calendarContainerStyling={{ padding: '10px', top: '5px' }}
              inputLabel={`Data Removal Date*`}
              disabledDates={disabledDataRemovalDates}
              labelInfo="Note: By default, all health metrics data (eg front/side view photos, pre/post-assessment values and file uploads, progress monitoring values) will be removed on this date, unless otherwise specified according to market legal requirements."
            />
          </div>
        )}
        {!isPromotion && (
          <>
            <div className={styles['input-container']}>
              <div className={styles['label-text']}>{t('admin.campaignConfig.campaignCalendarLink')}</div>
              <div className={styles['text-link-container']}>
                <div className={styles['switch-container']}>
                  <Toggle
                    toggleStyle={ToggleStyle.SWITCH}
                    disabled={false}
                    isActive={campaignData?.campaignTextLinkDisplay === 1}
                    onClick={() => {
                      setToggleValue(!toggleValue);
                      handleInputChangeNormal('campaignTextLinkDisplay', toggleValue ? 0 : 1);
                    }}
                  />
                </div>
                <TextArea
                  mlpVersion={MLPVersion.ONE}
                  value={campaignData?.campaignTextLink}
                  resize={false}
                  placeholder="Calendar link here"
                  onChange={e => {
                    handleInputChangeNormal('campaignTextLink', e.target.value);
                  }}
                />
              </div>
            </div>
            <div className={styles['input-container']}>
              <Input
                type={InputTypes.NUMBER}
                name={'hideSidebarDays'}
                label={`Hide Challenge in Sidebar (Number of Days After End Date)`}
                value={campaignData?.hideSidebarDays}
                mlpVersion={MLPVersion.ONE}
                onChange={e => {
                  handleInputChangeNormal('hideSidebarDays', e.target.value);
                  const dataRemovalDate = new Date(new Date(campaignData?.campaignEnd).getTime());
                  dataRemovalDate.setDate(
                    dataRemovalDate.getDate() + (180 + (e.target.value ? parseInt(e.target.value) : 0)),
                  );
                  handleInputChangeNormal('dataRemovalDate', dataRemovalDate);
                }}
                placeholder="e.g. 30"
              />
            </div>
            <div></div>
            <div className={styles['input-container']}>
              <Input
                type={InputTypes.NUMBER}
                name={'postAssessmentReminderDays'}
                label={`Activation of post-assessment section and notification (number of days before due date)`}
                value={campaignData?.postAssessmentReminderDays}
                mlpVersion={MLPVersion.ONE}
                onChange={e => {
                  handleInputChangeNormal('postAssessmentReminderDays', e.target.value);
                }}
                placeholder="e.g. 7"
              />
            </div>
          </>
        )}
      </div>

      {isPromotion ? (
        <Accordion
          key={'campaignMechanics'}
          id={'campaignMechanics'}
          iconType={ControlIconType.CHEVRON}
          title={`Campaign Mechanics`}
        >
          <>
            <div className={styles['edit-user-form-container']}>
              <div className={styles['input-container']}>
                <div className={styles['label-text']}>Challenge</div>
                <div className={styles['text-link-container']}>
                  <div className={styles['switch-container']}>
                    <Toggle
                      toggleStyle={ToggleStyle.SWITCH}
                      disabled={false}
                      isActive={campaignData?.isChallenge === 1}
                      onClick={() => {
                        const nextIsChallenge = !isChallenge;
                        setIsChallenge(nextIsChallenge);
                        handleInputChangeNormal('isChallenge', nextIsChallenge ? 1 : 0);
                        if (!nextIsChallenge) {
                          handleInputChangeNormal('promoChallengeId', '');
                          handleInputChangeNormal('redemptionCriteria', '');
                          setCampaignClickedFields(prevClickedFields => ({
                            ...prevClickedFields,
                            promoChallengeId: false,
                            redemptionCriteria: false,
                          }));
                        } else {
                          handleInputChangeNormal('productSKU', '');
                        }
                      }}
                      theme={Theme.PURPLE}
                    />
                  </div>
                </div>
              </div>
              <div className={styles['input-container']}>
                <div className={styles['label-text']}>Centralised Distribution Location</div>
                <div className={styles['text-link-container']}>
                  <div className={styles['switch-container']}>
                    <Toggle
                      toggleStyle={ToggleStyle.SWITCH}
                      disabled={!isChallenge ? false : true}
                      isActive={campaignData?.centralizedLocation === 1}
                      onClick={() => {
                        setCentralizedLocation(!centralizedLocation);
                        handleInputChangeNormal('centralizedLocation', centralizedLocation ? 0 : 1);
                      }}
                      theme={Theme.PURPLE}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className={styles['reward-header-label']}>{t('admin.campaignConfig.validation')}</div>
            {campaignData?.isChallenge === 1 ? (
              <div className={styles['edit-user-form-container']}>
              <div className={styles['input-container']}>
                <DropDown
                  name={'promoChallengeId'}
                  dropdownLabel={`${t('admin.campaignConfig.activeChallenge')}`}
                  required
                  mlpVersion={MLPVersion.ONE}
                  data={challengeList}
                  value={
                    challengeList.find(challenge => challenge.value === campaignData?.promoChallengeId)?.title ||
                    campaignData?.promoChallengeId
                  }
                  configData={{
                    label: 'title',
                    onSelect: (selectedOptions: DropDownDataInterface) => {
                      handleInputChangeNormal('promoChallengeId', selectedOptions.value);
                      setCampaignClickedFields(prevClickedFields => ({
                        ...prevClickedFields,
                        promoChallengeId: false,
                      }));
                    },
                  }}
                  onClick={() =>
                    setCampaignClickedFields(prevClickedFields => ({
                      ...prevClickedFields,
                      promoChallengeId: !campaignData?.promoChallengeId,
                    }))
                  }
                  status={
                    campaignClickedFields.promoChallengeId ? DropDownValidations.INVALID : DropDownValidations.DEFAULT
                  }
                  message={
                    campaignClickedFields.promoChallengeId ? t('admin.campaignConfig.activeChallengeReq') : ''
                  }
                />
              </div>
              <div className={styles['input-container']}>
                <DropDown
                  name={'redemptionCriteria'}
                  dropdownLabel={`${t('admin.campaignConfig.redemptionCriteria')}`}
                  required
                  mlpVersion={MLPVersion.ONE}
                  data={redemptionCriteriaList}
                  value={
                    redemptionCriteriaList.find(criteria => criteria.value === campaignData?.redemptionCriteria)
                      ?.title || campaignData?.redemptionCriteria
                  }
                  configData={{
                    label: 'title',
                    onSelect: (selectedOptions: DropDownDataInterface) => {
                      handleInputChangeNormal('redemptionCriteria', selectedOptions.value);
                      setCampaignClickedFields(prevClickedFields => ({
                        ...prevClickedFields,
                        redemptionCriteria: false,
                      }));
                    },
                  }}
                  onClick={() =>
                    setCampaignClickedFields(prevClickedFields => ({
                      ...prevClickedFields,
                      redemptionCriteria: !campaignData?.redemptionCriteria,
                    }))
                  }
                  status={
                    campaignClickedFields.redemptionCriteria
                      ? DropDownValidations.INVALID
                      : DropDownValidations.DEFAULT
                  }
                  message={
                    campaignClickedFields.redemptionCriteria ? t('admin.campaignConfig.redemptionCriteriaReq') : ''
                  }
                />
              </div>
              </div>
            ) : (
              <div className={styles['input-container']}>
                <Input
                  type={InputTypes.TEXT}
                  name={'productSKU'}
                  label={`${t('admin.campaignConfig.singleProductSKU')}`}
                  value={campaignData?.productSKU}
                  mlpVersion={MLPVersion.ONE}
                  onChange={e => {
                    handleInputChangeNormal('productSKU', e.target.value.toString());
                  }}
                />
              </div>
            )}
          </>
        </Accordion>
      ) : (
        <>
          {/* participant section  */}
          <Accordion
            key={'participantAccordion'}
            id={'participantAccordion'}
            iconType={ControlIconType.CHEVRON}
            title={t('admin.campaignConfig.participantTitle')}
            childrenTitle={
              <Tag
                theme={campaignData?.activities.participant.status === 'ACTIVE' ? Theme.SUCCESS : Theme.WARNING}
                style={TagStyle.OUTLINE}
                text={campaignData?.activities.participant.status}
              />
            }
          >
        <>
          <div className={styles['edit-user-form-container']}>
            {/* name  */}
            <div className={styles['input-container']}>
              <Input
                type={InputTypes.TEXT}
                name={'name'}
                placeholder={`${t('admin.campaignConfig.name')}`}
                label={`${t('admin.campaignConfig.name')}`}
                value={campaignData?.activities.participant.name}
                mlpVersion={MLPVersion.ONE}
                onChange={e => {
                  handleInputChangeNormal('activities.participant.name', e.target.value);
                }}
              />
            </div>
            {/* status */}
            <div className={styles['input-container']}>
              <div className={styles['label-text']}>{t('admin.campaignConfig.status')}</div>
              <div className={styles['radio-button-container']}>
                <RadioButton
                  label={t('admin.userEdit.active')}
                  checked={campaignData?.activities.participant.status === Status.ACTIVE}
                  onClick={() => {
                    handleInputChangeNormal('activities.participant.status', Status.ACTIVE);
                  }}
                />
                <RadioButton
                  label={t('admin.userEdit.inactive')}
                  checked={campaignData?.activities.participant.status === Status.INACTIVE}
                  onClick={() => {
                    handleInputChangeNormal('activities.participant.status', Status.INACTIVE);
                  }}
                />
              </div>
            </div>
            {/* description */}
            <div className={styles['input-container']}>
              <TextArea
                mlpVersion={MLPVersion.ONE}
                label={t('admin.campaignConfig.description')}
                resize={false}
                placeholder={t('admin.campaignConfig.description')}
                value={campaignData?.activities.participant.description}
                onChange={e => {
                  handleInputChangeNormal('activities.participant.description', e.target.value);
                }}
              />
            </div>
            {/* pin */}
            <div className={styles['input-container']}>
                            <DropDown
                id={'participantPin'}
                key={'participantPin'}
                name={'participantPin'}
                dropdownLabel={t('admin.campaignConfig.pin')}
                placeholder={t('admin.campaignConfig.pin')}
                mlpVersion={MLPVersion.ONE}
                data={pinIdSegmentList}
                multiSelection
                allSelectionOption
                value={campaignData?.activities.participant.pin.map(pin => pin.pinName)}
                configData={{
                  label: 'name',
                  onSelect: (selectedOptions: DropDownDataInterface[]) => {
                    const transformedOptions = selectedOptions.map(option => ({
                      pinId: option.id,
                      pinName: option.name,
                    }));
                    handleInputChangeNormal('activities.participant.pin', transformedOptions);
                  },
                }}
              />
            </div> 
            <div className={styles['input-container']}>
              <Input
                type={InputTypes.NUMBER}
                name={'teamMember'}
                label={`${t('admin.campaignConfig.teamMember')}`}
                value={campaignData?.activities.participant.teamSize}
                mlpVersion={MLPVersion.ONE}
                onChange={e => {
                  handleInputChangeNormal('activities.participant.teamSize', e.target.value);
                }}
              />
            </div>
            <div className={styles['input-container']}>
              <Input
                type={InputTypes.NUMBER}
                name={'participant days'}
                label={`${t('admin.campaignConfig.participantDays')}`}
                value={campaignData?.activities.participant.validity.value}
                mlpVersion={MLPVersion.ONE}
                onChange={e => {
                  handleInputChangeNormal('activities.participant.validity.value', e.target.value);
                }}
              />
            </div>
          </div>
          <div className={styles['reward-header-label']}>{t('admin.campaignConfig.newAboConfiguration')}</div>
          <div className={styles['edit-user-form-container']}>
            <div className={styles['text-link-container']}>
              <div className={styles['switch-container']}>
                <Toggle
                  toggleStyle={ToggleStyle.SWITCH}
                  disabled={false}
                  isActive={campaignData?.activities?.participant?.newAbo?.isRequired === 1}
                  onClick={() => {
                    // setToggleValue(!toggleValue);
                    handleInputChangeNormal(
                      'activities.participant.newAbo.isRequired',
                      campaignData?.activities?.participant?.newAbo?.isRequired ? 0 : 1,
                    );
                  }}
                />
              </div>
              <Input
                type={InputTypes.NUMBER}
                name={'numOfNewAbo'}
                label={`${t('admin.campaignConfig.numOfNewAbo')}`}
                value={campaignData?.activities.participant?.newAbo?.numOfNewAbo}
                mlpVersion={MLPVersion.ONE}
                onChange={e => {
                  handleInputChangeNormal('activities.participant.newAbo.numOfNewAbo', e.target.value);
                }}
              />
            </div>
            <div className={styles['input-container']}>
              <Input
                type={InputTypes.NUMBER}
                name={'numOfDays'}
                label={`${t('admin.campaignConfig.numOfDays')}`}
                value={campaignData?.activities.participant?.newAbo?.numOfDays}
                mlpVersion={MLPVersion.ONE}
                onChange={e => {
                  handleInputChangeNormal('activities.participant.newAbo.numOfDays', e.target.value);
                }}
              />
            </div>
          </div>
          <div className={styles['reward-header-label']}>{t('admin.campaignConfig.orderValidation')}</div>
          <div className={styles['edit-user-form-container']}>
            {/* points   */}
            <div className={styles['input-container']}>
              <Input
                type={InputTypes.NUMBER}
                name={'maxOrder'}
                label={`${t('admin.campaignConfig.maxOrder')}`}
                value={campaignData?.activities?.order?.maxOrderNumber}
                mlpVersion={MLPVersion.ONE}
                onChange={e => {
                  handleInputChangeFlatten('activities.order.maxOrderNumber', e.target.value.toString());
                }}
              />
            </div>
            <div className={styles['input-container']}>
              <Input
                type={InputTypes.NUMBER}
                name={'orderPeriod'}
                label={`${t('admin.campaignConfig.orderPeriod')}`}
                value={campaignData?.activities?.order?.orderPeriod}
                mlpVersion={MLPVersion.ONE}
                onChange={e => {
                  handleInputChangeFlatten('activities.order.orderPeriod', e.target.value.toString());
                }}
              />
            </div>
            <div className={styles['input-container']}>
              <Input
                type={InputTypes.NUMBER}
                name={'coachAbo'}
                label={`${t('admin.campaignConfig.orderAmount')}`}
                value={campaignData?.activities.order?.rules[0].value.amount}
                mlpVersion={MLPVersion.ONE}
                onChange={e => {
                  handleInputChangeFlatten('activities.order.rules.0.value.amount', e.target.value.toString());
                }}
              />
            </div>
            <div className={styles['input-container']}>
              <TextArea
                //type={InputTypes.TEXT} // Use TEXT type to handle comma-separated values
                name={'coachAbo'}
                label={t('admin.campaignConfig.itemList')}
                value={
                  Array.isArray(campaignData?.activities.order?.rules[2].value.base)
                    ? campaignData?.activities.order?.rules[2].value.base.join(',')
                    : campaignData?.activities.order?.rules[2].value.base
                }
                mlpVersion={MLPVersion.ONE}
                onChange={e => {
                  const inputValue = e.target.value;
                  const valuesArray = inputValue.split(',').map(item => item.trim());
                  handleInputChangeFlatten('activities.order.rules.2.value.base', valuesArray);
                }}
              />
            </div>
            <div className={styles['input-container']}>
              <Input
                type={InputTypes.TEXT} // Use TEXT type to handle comma-separated values
                name={'alpCount'}
                label={t('admin.campaignConfig.bundleList')}
                value={
                  Array.isArray(campaignData?.activities.order?.rules[1].value)
                    ? campaignData?.activities.order?.rules[1].value.join(',')
                    : campaignData?.activities.order?.rules[1].value
                }
                mlpVersion={MLPVersion.ONE}
                onChange={e => {
                  const inputValue = e.target.value;
                  const valuesArray = inputValue.split(',').map(item => item.trim());
                  handleInputChangeFlatten('activities.order.rules.1.value', valuesArray);
                }}
              />
            </div>
            {/* <div className={styles['input-container']}>
              <Input
                type={InputTypes.TEXT} // Use TEXT type to handle comma-separated values
                name={'alpCount'}
                label={t('admin.campaignConfig.addOnList')}
                value={
                  Array.isArray(campaignData?.activities.order?.rules[2].value.addOn)
                    ? campaignData?.activities.order?.rules[2].value.addOn.join(',')
                    : campaignData?.activities.order?.rules[2].value.addOn
                }
                mlpVersion={MLPVersion.ONE}
                onChange={e => {
                  const inputValue = e.target.value;
                  const valuesArray = inputValue.split(',').map(item => item.trim());
                  handleInputChangeFlatten('activities.order.rules.2.value.addOn', valuesArray);
                }}
              />
            </div> */}
          </div>
          {/* In  */}
          <div className={styles['reward-header-label']}>{t('admin.campaignConfig.in')}</div>
          <div className={styles['edit-user-form-container']}>
            <div className={styles['input-container']}>
              <TextArea
                mlpVersion={MLPVersion.ONE}
                label={t('admin.campaignConfig.description')}
                resize={false}
                placeholder={t('admin.campaignConfig.description')}
                value={campaignData?.activities.participant.rules[0].description}
                onChange={e => {
                  handleInputChangeFlatten('activities.participant.rules.0.description', e.target.value);
                }}
              />
            </div>
            <div className={styles['input-container-dropdown']}>
              <DropDown
                name={'rewardIn'}
                dropdownLabel={t('admin.campaignConfig.reward')}
                placeholder={t('admin.campaignConfig.reward')}
                mlpVersion={MLPVersion.ONE}
                data={rewardList}
                value={
                  campaignData?.activities.participant.rules[0].reward
                    ? rewardList
                        ?.find(
                          (i: DropDownData) => i.rewardCode === campaignData?.activities.participant.rules[0].reward,
                        )
                        ?.rewardName?.toString()
                    : ''
                }
                configData={{
                  label: 'rewardName',
                  onSelect: (selectedOption: DropDownData) => {
                    handleInputChangeFlatten('activities.participant.rules.0.reward', selectedOption.rewardCode);
                  },
                }}
              />
              <div className={styles['component-spacing']} />
              <DropDown
                name={'rewardInAbo'}
                dropdownLabel={t('admin.campaignConfig.rewardAbo')}
                placeholder={t('admin.campaignConfig.rewardAbo')}
                value={
                  campaignData?.activities.participant.rules[1].reward
                    ? rewardList
                        ?.find(
                          (i: DropDownData) => i.rewardCode === campaignData?.activities.participant.rules[1].reward,
                        )
                        ?.rewardName?.toString()
                    : ''
                }
                mlpVersion={MLPVersion.ONE}
                data={rewardList}
                configData={{
                  label: 'rewardName',
                  onSelect: (selectedOption: DropDownData) => {
                    handleInputChangeFlatten('activities.participant.rules.1.reward', selectedOption.rewardCode);
                  },
                }}
              />
            </div>
          </div>
          {/* Out        */}
          <div className={styles['reward-header-label']}>{t('admin.campaignConfig.out')}</div>
          <div className={styles['edit-user-form-container']}>
            <TextArea
              mlpVersion={MLPVersion.ONE}
              resize={false}
              label={t('admin.campaignConfig.description')}
              placeholder={t('admin.campaignConfig.description')}
              value={campaignData?.activities.participant.rules[2].description}
              onChange={e => {
                handleInputChangeFlatten('activities.participant.rules.2.description', e.target.value);
              }}
            />
            <DropDown
              name={'participantReward'}
              dropdownLabel={t('admin.campaignConfig.reward')}
              placeholder={t('admin.campaignConfig.reward')}
              value={
                campaignData?.activities.participant.rules[2].reward
                  ? rewardList
                      ?.find((i: DropDownData) => i.rewardCode === campaignData?.activities.participant.rules[2].reward)
                      ?.rewardName?.toString()
                  : ''
              }
              mlpVersion={MLPVersion.ONE}
              data={rewardList}
              configData={{
                label: 'rewardName',
                onSelect: (selectedOption: DropDownData) => {
                  handleInputChangeFlatten('activities.participant.rules.2.reward', selectedOption.rewardCode);
                },
              }}
            />
          </div>
          {/* winning criteria  */}
          <div className={styles['reward-header-label']}>{t('admin.participant.winningCriteria')}</div>
          <div className={styles['edit-user-form-container']}>
            <div className={styles['input-container']}>
              <TextArea
                mlpVersion={MLPVersion.ONE}
                label={t('admin.campaignConfig.description')}
                resize={false}
                placeholder={t('admin.campaignConfig.description')}
                value={campaignData?.activities.participant.rules[3].description}
                onChange={e => {
                  handleInputChangeFlatten('activities.participant.rules.3.description', e.target.value);
                }}
              />
            </div>
            <div className={styles['input-container']}>
              <DropDown
                name={'winningReward'}
                dropdownLabel={t('admin.campaignConfig.reward')}
                placeholder={t('admin.campaignConfig.reward')}
                value={
                  campaignData?.activities.participant.rules[3].reward
                    ? rewardList
                        ?.find(
                          (i: DropDownData) => i.rewardCode === campaignData?.activities.participant.rules[3].reward,
                        )
                        ?.rewardName?.toString()
                    : ''
                }
                mlpVersion={MLPVersion.ONE}
                data={rewardList}
                configData={{
                  label: 'rewardName',
                  onSelect: (selectedOption: DropDownData) => {
                    handleInputChangeFlatten('activities.participant.rules.3.reward', selectedOption.rewardCode);
                  },
                }}
              />
              <div className={styles['component-spacing']} />
              <DropDown
                name={'winningVoucher'}
                dropdownLabel={t('admin.campaignConfig.voucher')}
                placeholder={t('admin.campaignConfig.voucher')}
                value={
                  campaignData?.activities.participant.rules[3].voucher
                    ? rewardList
                        ?.find(
                          (i: DropDownData) => i.rewardCode === campaignData?.activities.participant.rules[3].voucher,
                        )
                        ?.rewardName?.toString()
                    : ''
                }
                mlpVersion={MLPVersion.ONE}
                data={rewardList}
                configData={{
                  label: 'rewardName',
                  onSelect: (selectedOption: DropDownData) => {
                    handleInputChangeFlatten('activities.participant.rules.3.voucher', selectedOption.rewardCode);
                  },
                }}
              />
            </div>
          </div>
          <div className={styles['reward-header-label']}>{t('admin.campaignConfig.criteriaPercentage')}</div>
          <div className={styles['edit-user-form-container-three-columns']}>
            {/* <Input
              type={InputTypes.NUMBER}
              name={'individualWeight'}
              label={`${t('admin.campaignConfig.individualWeight')}`}
              value={campaignData?.activities.participant.rules[4].subKpiCode[0].targetFrom}
              mlpVersion={MLPVersion.ONE}
              onChange={e => {
                handleInputChangeFlatten(
                  'activities.participant.rules.4.subKpiCode.0.targetFrom',
                  e.target.value.toString(),
                );
              }}
            /> */}
            <Input
              type={InputTypes.NUMBER}
              name={'individualFat'}
              label={`${t('admin.campaignConfig.individualFat')}`}
              value={campaignData?.activities.participant.rules[4].subKpiCode[1].targetFrom}
              mlpVersion={MLPVersion.ONE}
              onChange={e => {
                handleInputChangeFlatten(
                  'activities.participant.rules.4.subKpiCode.1.targetFrom',
                  e.target.value.toString(),
                );
              }}
            />
            <Input
              type={InputTypes.NUMBER}
              name={'individualMuscle'}
              label={`${t('admin.campaignConfig.individualMuscle')}`}
              value={campaignData?.activities.participant.rules[4].subKpiCode[2].targetFrom}
              mlpVersion={MLPVersion.ONE}
              onChange={e => {
                handleInputChangeFlatten(
                  'activities.participant.rules.4.subKpiCode.2.targetFrom',
                  e.target.value.toString(),
                );
              }}
            />
            {/* <Input
              type={InputTypes.NUMBER}
              name={'groupWeight'}
              label={`${t('admin.campaignConfig.groupWeight')}`}
              value={campaignData?.activities.participant.rules[3].subKpiCode[0].targetFrom}
              mlpVersion={MLPVersion.ONE}
              onChange={e => {
                handleInputChangeFlatten(
                  'activities.participant.rules.3.subKpiCode.0.targetFrom',
                  e.target.value.toString(),
                );
              }}
            />
            <Input
              type={InputTypes.NUMBER}
              name={'groupFat'}
              label={`${t('admin.campaignConfig.groupFat')}`}
              value={campaignData?.activities.participant.rules[3].subKpiCode[1].targetFrom}
              mlpVersion={MLPVersion.ONE}
              onChange={e => {
                handleInputChangeFlatten(
                  'activities.participant.rules.3.subKpiCode.1.targetFrom',
                  e.target.value.toString(),
                );
              }}
            />
            <Input
              type={InputTypes.NUMBER}
              name={'groupMuscle'}
              label={`${t('admin.campaignConfig.groupMuscle')}`}
              value={campaignData?.activities.participant.rules[3].subKpiCode[2].targetFrom}
              mlpVersion={MLPVersion.ONE}
              onChange={e => {
                handleInputChangeFlatten(
                  'activities.participant.rules.3.subKpiCode.2.targetFrom',
                  e.target.value.toString(),
                );
              }}
            /> */}
          </div>
        </>
      </Accordion>
      {/* coach section  */}
      <Accordion
        id={'coachAccordion'}
        key={'coachAccordion'}
        iconType={ControlIconType.CHEVRON}
        title={t('admin.campaignConfig.coachTitle')}
        childrenTitle={
          <Tag
            theme={campaignData?.activities.coach.status === 'ACTIVE' ? Theme.SUCCESS : Theme.WARNING}
            style={TagStyle.OUTLINE}
            text={campaignData?.activities.coach.status}
          />
        }
      >
        <>
          <div className={styles['edit-user-form-container']}>
            {/* name   */}
            <div className={styles['input-container']}>
              <Input
                type={InputTypes.TEXT}
                name={'name'}
                label={`${t('admin.campaignConfig.name')}`}
                placeholder={`${t('admin.campaignConfig.name')}`}
                value={campaignData?.activities.coach.name}
                mlpVersion={MLPVersion.ONE}
                onChange={e => {
                  handleInputChangeNormal('activities.coach.name', e.target.value);
                }}
              />
            </div>
            {/* status  */}
            <div className={styles['input-container']}>
              <div className={styles['label-text']}>{t('admin.campaignConfig.campaignStatus')}</div>
              <div className={styles['radio-button-container']}>
                <RadioButton
                  label={t('admin.userEdit.active')}
                  checked={campaignData?.activities.coach.status === Status.ACTIVE}
                  onClick={() => {
                    handleInputChangeNormal('activities.coach.status', Status.ACTIVE);
                  }}
                />
                <RadioButton
                  label={t('admin.userEdit.inactive')}
                  checked={campaignData?.activities.coach.status === Status.INACTIVE}
                  onClick={() => {
                    handleInputChangeNormal('activities.coach.status', Status.INACTIVE);
                  }}
                />
              </div>
            </div>
            {/* description  */}
            <div className={styles['input-container']}>
              <TextArea
                mlpVersion={MLPVersion.ONE}
                resize={false}
                label={t('admin.campaignConfig.description')}
                placeholder={t('admin.campaignConfig.description')}
                value={campaignData?.activities.coach.description}
                onChange={e => {
                  handleInputChangeNormal('activities.coach.description', e.target.value);
                }}
              />
            </div>
            {/* pin  */}
            <div className={styles['input-container']}>
             <DropDown
                id={'pinCoach'}
                key={'pinCoach'}
                name={'pin'}
                dropdownLabel={t('admin.campaignConfig.pinCoach')}
                placeholder={t('admin.campaignConfig.pinCoach')}
                value={campaignData?.activities.coach.pin.map(pin => pin.pinName)}
                mlpVersion={MLPVersion.ONE}
                data={pinIdSegmentList}
                multiSelection
                allSelectionOption
                configData={{
                  label: 'name',
                  onSelect: (selectedOptions: DropDownDataInterface[]) => {
                    const transformedOptions = selectedOptions.map(option => ({
                      pinId: option.id,
                      pinName: option.name,
                    }));
                    handleInputChangeNormal('activities.coach.pin', transformedOptions);
                  },
                }}
              />
            </div>
            {/* rolling month  
            <div className={styles['input-container']}>
              <Input
                type={InputTypes.NUMBER}
                name={'rollingMonth'}
                label={`${t('admin.campaignConfig.rollingMonths')}`}
                value={campaignData?.activities.coach.validity.value}
                mlpVersion={MLPVersion.ONE}
                onChange={e => {
                  handleInputChangeNormal('activities.coach.validity.value', e.target.value.toString());
                }}
              />
            </div>*/}
          </div>
          <div className={styles['reward-header-label']}>{t('admin.campaignConfig.criteria')}</div>
          <div className={styles['edit-user-form-container']}>
            {/* points   */}
            <div className={styles['input-container']}>
              <Input
                type={InputTypes.NUMBER}
                name={'completedCount'}
                label={`${t('admin.campaignConfig.completedCount')}`}
                value={campaignData?.activities.coach.rules[1].targetFrom}
                mlpVersion={MLPVersion.ONE}
                onChange={e => {
                  handleInputChangeFlatten('activities.coach.rules.1.targetFrom', e.target.value.toString());
                }}
              />
            </div>
            <div className={styles['input-container']}>
              <Input
                type={InputTypes.NUMBER}
                name={'achievedCount'}
                label={`${t('admin.campaignConfig.achievedCount')}`}
                value={campaignData?.activities.coach.rules[2].targetFrom}
                mlpVersion={MLPVersion.ONE}
                onChange={e => {
                  handleInputChangeFlatten('activities.coach.rules.2.targetFrom', e.target.value.toString());
                }}
              />
            </div>
            <div className={styles['input-container']}>
              <Input
                type={InputTypes.NUMBER}
                name={'alpCount'}
                label={`${t('admin.campaignConfig.alpCount')}`}
                value={campaignData?.activities?.coach?.rules[3]?.targetFrom}
                mlpVersion={MLPVersion.ONE}
                onChange={e => {
                  handleInputChangeFlatten('activities.coach.rules.3.targetFrom', e.target.value.toString());
                }}
              />
            </div>
            {/* abo  */}
            <div className={styles['input-container']}>
              <Input
                type={InputTypes.NUMBER}
                name={'coachAbo'}
                label={`${t('admin.campaignConfig.coachAbo')}`}
                value={campaignData?.activities.coach.rules[0].targetFrom}
                mlpVersion={MLPVersion.ONE}
                onChange={e => {
                  handleInputChangeFlatten('activities.coach.rules.0.targetFrom', e.target.value.toString());
                }}
              />
            </div>
          </div>
        </>
      </Accordion>
      {/* leader section  */}
      <Accordion
        id={'leaderAccordion'}
        key={'leaderAccordion'}
        iconType={ControlIconType.CHEVRON}
        title={t('admin.campaignConfig.leaderTitle')}
        childrenTitle={
          <Tag
            theme={campaignData?.activities.leader.status === 'ACTIVE' ? Theme.SUCCESS : Theme.WARNING}
            style={TagStyle.OUTLINE}
            text={campaignData?.activities.leader.status}
          />
        }
      >
        <>
          <div className={styles['edit-user-form-container']}>
            {/* name   */}
            <div className={styles['input-container']}>
              <Input
                type={InputTypes.TEXT}
                name={'leaderName'}
                label={`${t('admin.campaignConfig.name')}`}
                placeholder={`${t('admin.campaignConfig.name')}`}
                value={campaignData?.activities.leader.name}
                mlpVersion={MLPVersion.ONE}
                onChange={e => {
                  handleInputChangeNormal('activities.leader.name', e.target.value);
                }}
              />
            </div>
            {/* status  */}
            <div className={styles['input-container']}>
              <div className={styles['label-text']}>{t('admin.campaignConfig.campaignStatus')}</div>
              <div className={styles['radio-button-container']}>
                <RadioButton
                  label={t('admin.userEdit.active')}
                  checked={campaignData?.activities.leader.status === Status.ACTIVE}
                  onClick={() => {
                    handleInputChangeNormal('activities.leader.status', Status.ACTIVE);
                  }}
                />
                <RadioButton
                  label={t('admin.userEdit.inactive')}
                  checked={campaignData?.activities.leader.status === Status.INACTIVE}
                  onClick={() => {
                    handleInputChangeNormal('activities.leader.status', Status.INACTIVE);
                  }}
                />
              </div>
            </div>
            {/* description  */}
            <div className={styles['input-container']}>
              <TextArea
                mlpVersion={MLPVersion.ONE}
                resize={false}
                label={t('admin.campaignConfig.description')}
                placeholder={t('admin.campaignConfig.description')}
                value={campaignData?.activities.leader.description}
                onChange={e => {
                  handleInputChangeNormal('activities.leader.description', e.target.value);
                }}
              />
            </div>
            {/* pin  */}
            <div className={styles['input-container']}>
               <DropDown
                id={'leaderPin'}
                key={'leaderPin'}
                name={'leaderPin'}
                dropdownLabel={t('admin.campaignConfig.directLeaderPin')}
                placeholder={t('admin.campaignConfig.directLeaderPin')}
                value={campaignData?.activities.leader.pin.map(pin => pin.pinName)}
                mlpVersion={MLPVersion.ONE}
                data={pinIdSegmentList}
                multiSelection
                allSelectionOption
                configData={{
                  label: 'name',
                  onSelect: (selectedOptions: DropDownDataInterface[]) => {
                    const transformedOptions = selectedOptions.map(option => ({
                      pinId: option.id,
                      pinName: option.name,
                    }));
                    handleInputChangeNormal('activities.leader.pin', transformedOptions);
                  },
                }}
              />
            </div>
          </div>
          <div className={styles['reward-header-label']}>{t('admin.campaignConfig.criteria')}</div>
          <div className={styles['edit-user-form-container']}>
            {/* abo  */}
            <div className={styles['input-container']}>
              <Input
                type={InputTypes.NUMBER}
                name={'qualifyingCoach'}
                label={`${t('admin.campaignConfig.qualifyingCoach')}`}
                value={campaignData?.activities.leader.rules[0].targetFrom}
                mlpVersion={MLPVersion.ONE}
                onChange={e => {
                  handleInputChangeFlatten('activities.leader.rules.0.targetFrom', e.target.value.toString());
                }}
              />
            </div>
          </div>
          <div className={styles['invisible-line']} />
        </>
      </Accordion>
        </>
      )}
    </div>
  );
};
